import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Td,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaPen, FaTrash } from "react-icons/fa";
import {
  ContactPersonDto,
  OrganizationEntryDto,
  OrganizationEntryPitchDto,
  OrganizationType,
  useUpdateOrganizationEntryMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { canUpdateOrgEntry } from "../../../../common/auth/utils";
import { useApiError } from "../../../../common/errors/useApiError";
import { displayPhoneNumber } from "../../../../common/formatting/displayPhoneNumber";
import { OrganizationTypeTag } from "../../../../common/organization/OrganizationTypeTag";
import { useBaseData } from "../../../../common/useBaseData";

export const OrgEntryContactPersonRow = ({
  person,
  organizationEntry,
}: {
  person: ContactPersonDto;
  organizationEntry: OrganizationEntryDto | OrganizationEntryPitchDto;
}) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const baseData = useBaseData();
  const [updateOrganizationEntry, { isLoading }] = useUpdateOrganizationEntryMutation();
  const displayer = useApiError();

  const [editablePerson, setEditablePerson] = useState<ContactPersonDto>(person);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const canUpdate = canUpdateOrgEntry(authState.selectedOrg.roles);

  const handleDelete = async () => {
    await updateOrganizationEntry({
      entryId: organizationEntry.id,
      updateOrganizationEntryRequest: { removeContactPersonEmail: editablePerson.email },
    })
      .unwrap()
      .catch((e) => {
        displayer.trigger(e);
        throw e;
      });
    onDeleteClose();
  };

  const countryAlpha3Codes = baseData.countries.map((country) => country.alpha3Code);
  const callingCodes = baseData.callingCodes.filter(
    (callingCode) => countryAlpha3Codes.indexOf(callingCode.countryAlpha3Code) !== -1
  );
  const selectedCallingCodeAlpha3 = editablePerson.phoneNumber?.callingCode.countryAlpha3Code ?? "";

  const handleCallingCodeChange = (alpha3Code: string) => {
    const matchedCallingCode = callingCodes.find((code) => code.countryAlpha3Code === alpha3Code);
    setEditablePerson((current) => ({
      ...current,
      phoneNumber: {
        number: current.phoneNumber?.number ?? "",
        callingCode: {
          countryAlpha3Code: alpha3Code,
          value: matchedCallingCode?.value ?? "",
          countryName: matchedCallingCode?.countryName ?? "",
        },
      },
    }));
  };

  const handleNumberChange = (nextNumber: string) =>
    setEditablePerson((current) => {
      const callingCode = current.phoneNumber?.callingCode ?? {
        countryAlpha3Code: "",
        value: "",
        countryName: "",
      };

      return {
        ...current,
        phoneNumber: nextNumber ? { number: nextNumber, callingCode } : undefined,
      };
    });

  if (isFormVisible)
    return (
      <Tr key={editablePerson.email}>
        <Td>
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={editablePerson.name}
            onChange={(e) => setEditablePerson({ ...editablePerson, name: e.target.value })}
          />
        </Td>
        <Td>{editablePerson.email}</Td>
        <Td>
          <Flex alignItems="center" flexWrap="nowrap">
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                rightIcon={<ChevronDownIcon />}
                rounded="md"
                roundedRight="none"
                borderRightRadius="none"
                flexShrink={0}
                minW="20"
              >
                {selectedCallingCodeAlpha3
                  ? `+${callingCodes.find((code) => code.countryAlpha3Code === selectedCallingCodeAlpha3)?.value ?? ""}`
                  : t("Calling code")}
              </MenuButton>
              <Portal>
                <MenuList width="60" maxHeight="96" overflowY="auto">
                  {callingCodes.map((callingCode) => (
                    <MenuItem
                      key={callingCode.countryAlpha3Code + callingCode.value}
                      onClick={() => handleCallingCodeChange(callingCode.countryAlpha3Code)}
                    >
                      {callingCode.countryName} (+{callingCode.value})
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            <Input
              flex="0 0 24"
              width="24"
              size="sm"
              rounded="md"
              roundedLeft="none"
              borderLeft="none"
              backgroundColor="smBackground"
              placeholder={t("Phone number") ?? ""}
              type="tel"
              value={editablePerson.phoneNumber?.number ?? ""}
              onChange={(event) => handleNumberChange(event.target.value)}
            />
          </Flex>
        </Td>
        <Td minWidth="40">
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={editablePerson.title ?? ""}
            onChange={(e) => setEditablePerson({ ...editablePerson, title: e.target.value })}
          />
        </Td>
        <Td textAlign="center">{organizationEntry.name}</Td>
        <Td textAlign="center">
          <OrganizationTypeTag type={OrganizationType.OrganizationEntry} size="sm" />
        </Td>
        <Td>
          <IconButton
            aria-label="edit"
            icon={<Icon as={FaCheck} />}
            isLoading={isLoading}
            onClick={async () => {
              await updateOrganizationEntry({
                entryId: organizationEntry.id,
                updateOrganizationEntryRequest: {
                  editContactPerson: editablePerson,
                },
              })
                .unwrap()
                .catch((e) => {
                  displayer.trigger(e);
                  throw e;
                });
              setIsFormVisible(false);
            }}
          />
        </Td>
      </Tr>
    );

  return (
    <>
      <Tr key={editablePerson.email}>
        <Td>{editablePerson.name}</Td>
        <Td>{editablePerson.email}</Td>
        <Td whiteSpace="nowrap">{editablePerson.phoneNumber ? displayPhoneNumber(editablePerson.phoneNumber) : null}</Td>
        <Td>{editablePerson.title}</Td>
        <Td textAlign="center">{organizationEntry.name}</Td>
        <Td textAlign="center">
          <OrganizationTypeTag type={OrganizationType.OrganizationEntry} size="sm" />
        </Td>
        <Td>
          <Flex gap={2}>
            <Tooltip label={t("Edit contact person")}>
              <IconButton
                aria-label={t("Edit contact person")}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                isDisabled={!canUpdate}
                icon={<Icon as={FaPen} />}
                onClick={() => setIsFormVisible(true)}
              />
            </Tooltip>
            <Tooltip label={t("Delete contact person")}>
              <IconButton
                aria-label={t("Delete contact person")}
                size="sm"
                variant="ghost"
                colorScheme="red"
                isDisabled={!canUpdate}
                icon={<Icon as={FaTrash} />}
                onClick={onDeleteOpen}
              />
            </Tooltip>
          </Flex>
        </Td>
      </Tr>
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("Delete Contact Person")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {`${t("Are you sure you want to delete this contact person")}? ${t("This action cannot be undone")}.`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" ml={3} isLoading={isLoading} onClick={handleDelete}>
                {t("Delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
